import EmberService, { service } from '@ember/service';

import type { APIData, Service } from 'webapp-types';
import type { TrackingEvent } from 'webapp/enums/tracking';
import { generateNormalizedString } from 'webapp/utils/common';

// an event to track later (e.g. by a shared component, on change)
export interface TrackedEvent {
	event: TrackingEvent;
	data?: APIData.Tracking.Data;
}

export default class AnalyticsService extends EmberService {
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('request') declare requestService: Service.Request;

	private logTracking = false;
	private sendTracking = true;

	private _logTracking(type: string, ...params: (APIData.Tracking.Data | string)[]): void {
		if (this.logTracking) {
			// eslint-disable-next-line no-console
			console.log('TRACKING', type, ...params);
		}
	}

	private sendRequest(type: string, data: APIData.Tracking.Data, group?: string[]): void {
		const payload: APIData.Tracking.Data = { type, data };

		if (group) {
			// default is ['user'], can be e.g. ['user', 'company'] to track on both
			payload.group = group;
		}

		this._logTracking(type, payload);

		if (this.sendTracking && this.currentUserService?.accessToken) {
			this.requestService.api('POST', '/tracking', payload).catch(() => {
				//
			});
		}
	}

	// list views track their view switch as `${Name} changed view`
	event(event: TrackingEvent | `${string} changed view`, data?: APIData.Tracking.Data): void {
		const requestData: APIData.Tracking.Data = {
			event: `_${event}`,
		};

		if (data) {
			requestData.data = data;
		}

		this.sendRequest('event', requestData);
	}

	page(category: string, data?: APIData.Tracking.Data): void {
		const requestData: APIData.Tracking.Data = {
			page: `_${category}`,
		};

		if (data) {
			requestData.data = data;
		}

		this.sendRequest('page', requestData);
	}

	properties(properties: APIData.Tracking.Data, group?: string[]): void {
		this.sendRequest('property', properties, group);
	}

	pageFromPath(path: string): void {
		const pathName = this.getTrackingPathName(path);

		if (pathName) {
			this.page(pathName);
		}
	}

	private getTrackingPathName(path: string): string | null {
		const databoardPageName = this.currentUserService.isOnboarded
			? 'Databoard setup'
			: 'Onboarding';

		switch (path) {
			case 'index':
				return 'Home';

			case 'settings.users':
				return 'User management';
			case 'settings.billing.index':
				return 'Billing';
			case 'settings.billing.plans':
				return 'Plans';
			case 'settings.level.index':
				return 'Account Progress';
			case 'settings.logs':
				return 'Activity log';
			case 'settings.notifications':
				return 'Notifications';

			case 'wizard.source':
				return databoardPageName + ' Source';
			case 'wizard.metrics':
				return databoardPageName + ' Metrics';
			case 'wizard.connect':
				return databoardPageName + ' Connect';
			case 'wizard.starting':
				return databoardPageName + ' Source Steps';
			case 'wizard.create-metric':
				return databoardPageName + ' Create Metric';
			case 'wizard.create-block':
				return databoardPageName + ' Create Block';

			case 'datamanager.available.index':
				return 'Available connections';
			case 'datamanager.connected.index':
				return 'My Data Sources';
			case 'datamanager.datasets':
				return 'Datasets';
			case 'datamanager.connections':
				return 'Connections';

			case 'designer':
				return 'Designer';

			case 'boards.index':
				return 'Databoards';
			case 'boards.public-templates.index':
				return 'Templates';
			case 'boards.public-templates.template.index':
				return 'Template preview';
			case 'boards.account-templates':
				return 'Account Templates';
			case 'boards.view.board':
				return 'Databoard in-app fullscreen';
			case 'boards.loops.loop':
				return 'Loop in-app fullscreen';
			case 'boards.loops.index':
				return 'Loops';

			case 'goals.my':
				return 'Goals';
			case 'goals.team':
				return 'Team Goals';
			case 'goals.company':
				return 'Company Goals';
			case 'goals.insights.detail':
				return 'Goal Insights';
			case 'goals.strategy':
				return 'OKR Strategy';

			case 'reports.index':
				return 'Reports';
			case 'reports.schedules':
				return 'Reports Schedules';
			case 'reports.archive':
				return 'Reports Archive';
			case 'reports.editor':
				return 'Reports Editor';
			case 'reports.public-templates.index':
				return 'Report Templates';
			case 'reports.public-templates.template.index':
				return 'Report Template preview';
			case 'reports.account-templates':
				return 'Report Account Templates';
			case 'reports.view.report':
				return 'Reports in-app fullscreen';

			case 'metric-library.index':
				return 'Metric library popular';
			case 'metric-library.source.specific':
				return 'Metric library by source';
			case 'metrics.index':
				return 'Metrics';
			case 'metrics.detail':
				return 'Metric Detail';
			case 'metrics.details':
				return 'Metric Insights';

			case 'clients.accounts.index':
				return 'Client accounts';
			case 'clients.performance':
				return 'Client performance';
			case 'clients.goals':
				return 'Client goals';

			case 'teams.index':
				return 'Team overview';
			case 'teams.view':
				return 'Team';

			default:
				return null;
		}
	}

	getNormalizedTrackingPathName(path: string) {
		const trackingPathName = this.getTrackingPathName(path);
		return trackingPathName ? generateNormalizedString(trackingPathName, true) : null;
	}

	initializeFromEnv(): void {
		this.sendTracking = window.env !== 'local';

		// uncomment for testing
		// this.sendTracking = true;
		// this.logTracking = true;
	}
}

declare module '@ember/service' {
	interface Registry {
		analytics: AnalyticsService;
	}
}
